import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getDateRangeFromTime } from "@/utils/helperFunctions";
import {
  fetchAlerts,
  markAlertRead,
  markAlertUnread,
  markAllAlertsRead,
} from "../api/alerts.api";
import { useAuth } from "@/features/auth";
import { useDeepCompareMemo } from "use-deep-compare";

export function useAlerts(filters = {}, pollInterval = 300000) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const alertsBaseKey = ["alerts", user?._id];

  const normalizedFilters = useDeepCompareMemo(() => {
    if (!filters?.timeRange) {
      return filters;
    }

    const result = { ...filters };

    if (result?.timeRange) {
      const { startDate, endDate } = getDateRangeFromTime(filters.timeRange);
      result.from = startDate;
      result.to = endDate;
      delete result.timeRange;
    }

    return result;
  }, [filters]);

  const alertsQueryKey = [...alertsBaseKey, normalizedFilters];

  const alertsQuery = useQuery({
    queryKey: alertsQueryKey,
    queryFn: async () => {
      if (!user?._id) {
        return { data: [] };
      }

      return fetchAlerts(normalizedFilters);
    },
    enabled: !!user?._id,
    refetchInterval: pollInterval,
    refetchIntervalInBackground: false,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    staleTime: pollInterval / 2,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const updateAlertInCache = (updater, scopedStudentId) => {
    const prevEntries = queryClient.getQueriesData({
      queryKey: alertsBaseKey,
    });

    prevEntries.forEach(([key, old]) => {
      if (!old?.data) {
        return;
      }

      queryClient.setQueryData(key, {
        ...old,
        data: old.data.map((data) => {
          if (
            scopedStudentId &&
            data.student._id.toString() !== scopedStudentId.toString()
          ) {
            return data;
          }
          return {
            ...data,
            alerts: data.alerts.map((alert) => updater(alert)),
          };
        }),
      });
    });

    return { prevEntries };
  };

  const rollback = (context) => {
    (context?.prevEntries || []).forEach(([key, prev]) => {
      queryClient.setQueryData(key, prev);
    });
  };

  const markAsReadMutation = useMutation({
    mutationFn: markAlertRead,
    onMutate: async (alertId) => {
      await queryClient.cancelQueries({ queryKey: alertsBaseKey });

      return updateAlertInCache((alert) =>
        alert._id === alertId
          ? {
              ...alert,
              read: true,
              readAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }
          : alert,
      );
    },
    onError: (error, variables, context) => rollback(context),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: alertsBaseKey }),
  });

  const markAsUnreadMutation = useMutation({
    mutationFn: markAlertUnread,
    onMutate: async (alertId) => {
      await queryClient.cancelQueries({ queryKey: alertsBaseKey });

      return updateAlertInCache((alert) =>
        alert._id === alertId
          ? {
              ...alert,
              read: false,
              readAt: null,
              updatedAt: new Date().toISOString(),
            }
          : alert,
      );
    },
    onError: (error, variables, context) => rollback(context),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: alertsBaseKey }),
  });

  const markAllReadMutation = useMutation({
    mutationFn: (studentId) => markAllAlertsRead(studentId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: alertsBaseKey });

      return updateAlertInCache((alert) =>
        alert.read
          ? alert
          : {
              ...alert,
              read: true,
              readAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
      );
    },
    onError: (error, variables, context) => rollback(context),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: alertsBaseKey }),
  });

  return {
    alerts: alertsQuery?.data?.data || null,
    isLoading: alertsQuery.isLoading,
    error: alertsQuery.error,
    refetch: alertsQuery.refetch,
    markAlertAsRead: markAsReadMutation.mutateAsync,
    markAlertAsUnread: markAsUnreadMutation.mutateAsync,
    markAllAlertsAsRead: markAllReadMutation.mutateAsync,
  };
}
